import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { VariantSelector } from "@/components/QuickView";
import { ProductCard } from "@/components/ProductCard";
import { Grid } from "./Home";
import { money, collectionLabel, deptLabel } from "@/data/site";
import { Heart, Minus, Plus, Ruler, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useSeo, SITE, absUrl } from "@/hooks/useSeo";

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, inWishlist } = useApp();
  const [product, setProduct] = useState(null);
  const [recs, setRecs] = useState([]);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQtyLocal] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false); setSize(null); setQtyLocal(1); setActiveImg(0);
    api.get(`/products/${slug}`).then((r) => { setProduct(r.data); setColor(r.data.colors?.[0]); })
      .catch(() => setNotFound(true));
    api.get(`/products/${slug}/recommendations`).then((r) => setRecs(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  useSeo(product ? {
    title: `${product.name} — HYEAMAN`,
    description: (product.description || `${product.name} by HYEAMAN — luxury editorial streetwear.`).slice(0, 160),
    path: `/product/${product.slug}`,
    image: product.images?.[0],
    jsonLd: [{
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: (product.images || []).map(absUrl),
      description: (product.description || `${product.name} by HYEAMAN.`).slice(0, 400),
      brand: { "@type": "Brand", name: "HYEAMAN" },
      category: product.category,
      sku: product.variants?.[0]?.sku || product.slug,
      offers: {
        "@type": "Offer",
        url: `${SITE}/product/${product.slug}`,
        priceCurrency: "CAD",
        price: product.price,
        priceValidUntil: `${new Date().getFullYear() + 2}-12-31`,
        itemCondition: "https://schema.org/NewCondition",
        availability: `https://schema.org/${product.total_stock > 0 ? "InStock" : "OutOfStock"}`,
        hasMerchantReturnPolicy: { "@id": product.collection === "black-label" ? `${SITE}/#returnpolicy-finalsale` : `${SITE}/#returnpolicy` },
        shippingDetails: {
          "@type": "OfferShippingDetails",
          shippingDestination: { "@type": "DefinedRegion", addressCountry: "CA" },
          deliveryTime: { "@type": "ShippingDeliveryTime", transitTime: { "@type": "QuantitativeValue", minValue: 2, maxValue: 4, unitCode: "DAY" } },
          shippingRate: { "@type": "MonetaryAmount", value: 15, currency: "CAD" },
          hasShippingService: { "@id": `${SITE}/#shippingservice-ca` },
        },
      },
    }, {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Shop", item: `${SITE}/shop` },
        { "@type": "ListItem", position: 3, name: product.name, item: `${SITE}/product/${product.slug}` },
      ],
    }],
  } : { title: "HYEAMAN — Wear Confidence" });

  if (notFound) return (
    <div className="flex flex-col items-center justify-center py-40 text-center">
      <p className="font-serif text-3xl text-cream">Piece not found</p>
      <Link to="/shop" className="mt-6 text-xs uppercase tracking-widest text-gold">Return to store</Link>
    </div>
  );
  if (!product) return <div className="py-40 text-center text-cream/50">Loading…</div>;

  const variant = product.variants?.find((v) => v.color === color && v.size === size);
  const hasColorViews = !!(product.color_images && product.color_images[color]);
  const views = hasColorViews ? product.color_images[color] : product.images;
  const colorwayMode = !product.color_images && product.colors?.length > 1 && product.colors.length === product.images?.length;
  const viewLabel = (src, i) => {
    if (colorwayMode) return product.colors[i];
    if (product.category === "Suits") return i === 0 ? "Jacket" : "Trousers";
    const s = (src || "").toLowerCase();
    if (s.includes("back")) return "Back";
    if (s.includes("side")) return "Side";
    if (s.includes("multi")) return "Full";
    return i === 0 ? "Front" : "View";
  };
  const selectColor = (c) => {
    setColor(c);
    if (product.color_images && product.color_images[c]) setActiveImg(0);
    else if (colorwayMode) { const i = product.colors.indexOf(c); if (i >= 0) setActiveImg(i); }
  };

  const soldOut = (product.total_stock ?? 0) <= 0;
  const handleAdd = () => {
    if (soldOut) { toast.error("This piece is not yet available"); return; }
    if (!size) { toast.error("Please select a size"); return; }
    addToCart(product, variant, qty);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8 lg:px-12" data-testid="product-page">
      <nav className="mb-8 flex items-center gap-2 text-xs text-cream/40">
        <Link to="/" className="hover:text-gold">Home</Link><ChevronRight size={12} />
        <Link to="/shop" className="hover:text-gold">Shop</Link><ChevronRight size={12} />
        <span className="text-cream/70">{product.name}</span>
      </nav>

      <div className="grid gap-10 lg:grid-cols-2">
        {/* GALLERY */}
        <div className="flex flex-col-reverse gap-4 sm:flex-row">
          <div className="flex gap-3 sm:flex-col" data-testid="product-thumbnails">
            {views?.map((img, i) => (
              <button key={img} onClick={() => { setActiveImg(i); if (colorwayMode) setColor(product.colors[i]); }} data-testid={`thumbnail-${i}`}
                className={`relative h-20 w-16 overflow-hidden border transition ${activeImg === i ? "border-gold" : "border-white/10 hover:border-gold/50"}`}>
                <img src={img} alt={`${product.name} ${viewLabel(img, i)}`} className="h-full w-full object-cover" />
                <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-center text-[0.5rem] uppercase tracking-widest text-cream/70">{viewLabel(img, i)}</span>
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-hidden border border-white/10 bg-ink">
            <img src={views?.[activeImg] || product.images?.[0]} alt={product.name} className="aspect-[3/4] w-full object-cover" data-testid="product-main-image" />
          </div>
        </div>

        {/* DETAILS */}
        <div>
          <div className="flex items-center gap-3">
            <p className="overline-label">{collectionLabel(product.collection)}</p>
            {product.badge && <span className="bg-gold px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-widest text-black">{product.badge}</span>}
          </div>
          <h1 className="mt-3 font-serif text-3xl text-cream sm:text-4xl" data-testid="product-name">{product.name}</h1>
          <p className="mt-1 text-xs uppercase tracking-widest text-cream/40">{deptLabel(product.department)}</p>

          <div className="mt-4 flex items-center gap-3">
            <span className="font-mono text-2xl text-gold" data-testid="product-price">{money(product.price)}</span>
            {product.previous_price && <span className="font-mono text-base text-mutedgray line-through">{money(product.previous_price)}</span>}
          </div>

          <p className="mt-6 text-sm leading-relaxed text-cream/70">{product.description}</p>

          <div className="mt-8"><VariantSelector product={product} color={color} setColor={selectColor} size={size} setSize={setSize} /></div>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-white/15">
              <button onClick={() => setQtyLocal(Math.max(1, qty - 1))} aria-label="Decrease" className="px-3 py-3 text-cream hover:text-gold"><Minus size={14} /></button>
              <span className="w-10 text-center text-cream" data-testid="product-qty">{qty}</span>
              <button onClick={() => setQtyLocal(qty + 1)} aria-label="Increase" className="px-3 py-3 text-cream hover:text-gold"><Plus size={14} /></button>
            </div>
            <button onClick={handleAdd} disabled={soldOut} data-testid="add-to-bag-button"
              className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest transition ${soldOut ? "cursor-not-allowed bg-white/10 text-cream/40" : "bg-gold text-black hover:bg-gold-bright"}`}>
              {soldOut ? "Coming Soon" : "Add to Bag"}
            </button>
            <button onClick={() => toggleWishlist(product)} aria-label="Wishlist" data-testid="product-wishlist"
              className={`px-4 py-4 border transition ${inWishlist(product.id) ? "border-gold bg-gold/10 text-gold" : "border-white/20 text-cream hover:border-gold"}`}>
              <Heart size={16} fill={inWishlist(product.id) ? "currentColor" : "none"} />
            </button>
          </div>

          <Link to="/size-guide" className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cream/50 hover:text-gold" data-testid="size-guide-link">
            <Ruler size={14} /> Size Guide
          </Link>

          <div className="mt-8 space-y-2 border-t border-white/10 pt-6 text-xs text-cream/50">
            <p>· Complimentary shipping on orders over C$250</p>
            <p>· 30-day returns · Signature gift packaging</p>
          </div>
        </div>
      </div>

      {recs.length > 0 && (
        <section className="mt-24">
          <h2 className="mb-8 font-black uppercase tracking-tight text-cream text-2xl sm:text-3xl">You May Also Like</h2>
          <Grid>{recs.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</Grid>
        </section>
      )}
    </div>
  );
}
